import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, TrendingDown, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const RepaymentNpaReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch !== 'all') params.branch_id = branch;
      const resp = await api.get('/reports/repayment-npa', { params });
      setData(resp.data);
    } catch { toast.error('Failed to fetch report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => branch === 'all' ? 'All Branches' : branches.find(b => b.id === branch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!data) { toast.error('Generate the report first'); return; }
    const sm = data.summary || {};
    const branchRows = (data.branch_summary || []).map(b => ({
      'Branch': b.branch_name,
      'Total Loans': b.total_loans || 0,
      'Disbursed': Math.round(b.total_disbursed || 0),
      'Collected': Math.round(b.total_collected || 0),
      'Repayment %': Number((b.repayment_percentage || 0).toFixed(2)),
      'NPA Count': b.npa_count || 0,
      'NPA %': Number((b.npa_percentage || 0).toFixed(2)),
    }));
    const loanRows = (data.individual_loans || []).map((l, i) => ({
      'SN': i + 1,
      'Loan ID': l.loan_id,
      'Entrepreneur': l.entrepreneur_name,
      'Branch': l.branch_name || '',
      'Loan Amount': Math.round(l.loan_amount || 0),
      'Collected': Math.round(l.actual_collected || 0),
      'Repayment %': Number((l.repayment_percentage || 0).toFixed(2)),
      'NPA': l.is_npa ? 'YES' : 'NO',
    }));
    exportToExcel({
      filename: `WWAGS_Repayment_NPA_${getBranchName().replace(/\s+/g,'_')}`,
      sheets: [
        { name: 'Summary', rows: [
          { Metric: 'Branch', Value: getBranchName() },
          { Metric: 'Total Loans', Value: sm.total_loans || 0 },
          { Metric: 'Total Disbursed', Value: Math.round(sm.total_disbursed || 0) },
          { Metric: 'Total Collected', Value: Math.round(sm.total_collected || 0) },
          { Metric: 'Portfolio Repayment %', Value: Number((sm.portfolio_repayment_percentage || 0).toFixed(2)) },
          { Metric: 'NPA Count', Value: sm.npa_count || 0 },
          { Metric: 'NPA %', Value: Number((sm.npa_percentage || 0).toFixed(2)) },
        ]},
        { name: 'Branch-wise', rows: branchRows },
        { name: 'Individual Loans', rows: loanRows },
      ],
    });
  };

  const handlePrint = () => {
    if (!data) return;
    const sm = data.summary || {};
    const branchRows = (data.branch_summary||[]).map((b, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${b.branch_name}</td><td style="text-align:center">${b.total_loans||0}</td><td style="text-align:right">Rs.${(b.total_disbursed||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(b.total_collected||0).toLocaleString('en-IN')}</td><td style="text-align:center">${(b.repayment_percentage||0).toFixed(2)}%</td><td style="text-align:center">${b.npa_count||0}</td><td style="text-align:center">${(b.npa_percentage||0).toFixed(0)}%</td></tr>`).join('');
    const loanRows = (data.individual_loans||[]).map((l, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${l.loan_id}</td><td>${l.entrepreneur_name}</td><td>${l.branch_name||'-'}</td><td style="text-align:right">Rs.${(l.loan_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(l.actual_collected||0).toLocaleString('en-IN')}</td><td style="text-align:center">${(l.repayment_percentage||0).toFixed(1)}%</td><td style="text-align:center">${l.is_npa?'YES':'-'}</td></tr>`).join('');
    const body = `<div class="cards"><div class="card"><small>Total Loans</small><b>${sm.total_loans||0}</b></div><div class="card"><small>Total Disbursed</small><b>Rs.${(sm.total_disbursed||0).toLocaleString('en-IN')}</b></div><div class="card"><small>Total Collected</small><b>Rs.${(sm.total_collected||0).toLocaleString('en-IN')}</b></div><div class="card"><small>Portfolio Repayment</small><b>${(sm.portfolio_repayment_percentage||0).toFixed(2)}%</b></div><div class="card"><small>NPA Count</small><b>${sm.npa_count||0}</b></div><div class="card"><small>NPA %</small><b>${(sm.npa_percentage||0).toFixed(1)}%</b></div></div><h3>Branch-wise Summary</h3><table><thead><tr><th>Branch</th><th>Loans</th><th>Disbursed</th><th>Collected</th><th>Repayment %</th><th>NPA Count</th><th>NPA %</th></tr></thead><tbody>${branchRows}</tbody></table><h3>Individual Loans</h3><table><thead><tr><th>Loan ID</th><th>Entrepreneur</th><th>Branch</th><th>Loan Amount</th><th>Collected</th><th>Repayment %</th><th>NPA</th></tr></thead><tbody>${loanRows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('Repayment & NPA Report', body, getBranchName()));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="repayment-npa-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">Repayment & NPA Report</h1><p className="text-sm text-slate-600">Individual & branch-wise repayment %, NPA & defaulters</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        {isAdmin() && <Select value={branch} onValueChange={setBranch}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent></Select>}
        <Button onClick={fetchReport} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="fetch-repayment-npa">Generate</Button>
        {data && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {data && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div></div>}

      {data && !loading && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center"><p className="text-xs text-slate-600 mb-1">Total Loans</p><p className="text-xl font-bold">{data.summary?.total_loans}</p></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center"><p className="text-xs text-emerald-600 mb-1">Portfolio Repayment</p><p className="text-xl font-bold text-emerald-800">{(data.summary?.portfolio_repayment_percentage||0).toFixed(1)}%</p></div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center"><p className="text-xs text-red-600 mb-1">NPA Count</p><p className="text-xl font-bold text-red-800">{data.summary?.npa_count}</p></div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center"><p className="text-xs text-amber-600 mb-1">NPA %</p><p className="text-xl font-bold text-amber-800">{(data.summary?.npa_percentage||0).toFixed(1)}%</p></div>
          </div>

          {(data.branch_summary||[]).length > 0 && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-slate-900 mb-2">Branch-wise Summary</h3>
              <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700 text-white"><tr><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-center">Loans</th><th className="px-3 py-2 text-right">Disbursed</th><th className="px-3 py-2 text-right">Collected</th><th className="px-3 py-2 text-center">Repayment %</th><th className="px-3 py-2 text-center">NPA</th><th className="px-3 py-2 text-center">NPA %</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.branch_summary.map((b, i) => (
                      <tr key={i} className={i % 2 === 0 ? '' : 'bg-slate-50'}>
                        <td className="px-3 py-2 font-medium">{b.branch_name}</td>
                        <td className="px-3 py-2 text-center">{b.total_loans}</td>
                        <td className="px-3 py-2 text-right">Rs.{(b.total_disbursed||0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">Rs.{(b.total_collected||0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-center font-bold">{(b.repayment_percentage||0).toFixed(1)}%</td>
                        <td className="px-3 py-2 text-center text-red-600 font-bold">{b.npa_count}</td>
                        <td className="px-3 py-2 text-center">{(b.npa_percentage||0).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <h3 className="text-base font-semibold text-slate-900 mb-2">Individual Loans</h3>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-600 text-white"><tr><th className="px-3 py-2 text-left">Loan ID</th><th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-right">Loan Amt</th><th className="px-3 py-2 text-right">Collected</th><th className="px-3 py-2 text-center">Repay %</th><th className="px-3 py-2 text-center">NPA</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(data.individual_loans||[]).map((l, i) => (
                  <tr key={l.loan_id} className={`${i % 2 === 0 ? '' : 'bg-slate-50'} hover:bg-slate-100`}>
                    <td className="px-3 py-2"><Link to={`/loans/${l.loan_id}`} className="text-blue-700 hover:underline font-medium">{l.loan_id}</Link></td>
                    <td className="px-3 py-2">{l.entrepreneur_name}</td>
                    <td className="px-3 py-2 text-slate-600">{l.branch_name}</td>
                    <td className="px-3 py-2 text-right">Rs.{(l.loan_amount||0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">Rs.{(l.actual_collected||0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-center font-bold">{(l.repayment_percentage||0).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-center">{l.is_npa ? <span className="text-red-600 font-bold">YES</span> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !loading && <div className="text-center py-12 text-slate-500"><TrendingDown className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Click "Generate" to view the report</p></div>}
    </div>
  );
};

export default RepaymentNpaReport;
